import type { Locale } from "@/lib/locale";

/**
 * Locale for DATA LABELS, which is a superset of the UI locales: the chat can
 * run in a language the site has no UI for, and its chips still need province
 * and language names. `Locale` widens automatically with `routing.locales`;
 * the extra three are canned chat languages (`CANNED_LANGS`).
 */
export type DataLabelLocale = Locale | "ja" | "zh" | "ko";
/**
 * Language code helpers — pure functions, safe to import in both
 * server components and client components.
 */

/** ISO 639-1/variant code → English display label */
export const LANG_DISPLAY: Record<string, string> = {
  en:  "English",    vi:  "Vietnamese", fr:  "French",
  ja:  "Japanese",   zh:  "Mandarin",   yue: "Cantonese",
  ko:  "Korean",     ru:  "Russian",    es:  "Spanish",
  de:  "German",     it:  "Italian",    pt:  "Portuguese",
  th:  "Thai",       id:  "Indonesian", ms:  "Malay",
  tl:  "Tagalog",
};

/** ISO code → Vietnamese display label (Tiếng + …). */
export const LANG_DISPLAY_VI: Record<string, string> = {
  en:  "Tiếng Anh",         vi:  "Tiếng Việt",     fr:  "Tiếng Pháp",
  ja:  "Tiếng Nhật",        zh:  "Tiếng Trung",    yue: "Tiếng Quảng Đông",
  ko:  "Tiếng Hàn",         ru:  "Tiếng Nga",      es:  "Tiếng Tây Ban Nha",
  de:  "Tiếng Đức",         it:  "Tiếng Ý",        pt:  "Tiếng Bồ Đào Nha",
  th:  "Tiếng Thái",        id:  "Tiếng Indonesia", ms: "Tiếng Mã Lai",
  tl:  "Tiếng Tagalog",
};

/** ISO code → French display label. Added 2026-08-24: the assistant renders a
 *  language NAME inside its own sentence ("Voici 3 professionnels ... qui
 *  parlent {lang}"), and the English fallback put "Korean" in the middle of a
 *  French one. Lower-case is correct for French language names. */
export const LANG_DISPLAY_FR: Record<string, string> = {
  en:  "anglais",     vi:  "vietnamien", fr:  "français",
  ja:  "japonais",    zh:  "mandarin",   yue: "cantonais",
  ko:  "coréen",      ru:  "russe",      es:  "espagnol",
  de:  "allemand",    it:  "italien",    pt:  "portugais",
  th:  "thaï",        id:  "indonésien", ms:  "malais",
  tl:  "tagalog",
};


/** ISO code → Japanese / Chinese / Korean display labels. The assistant renders
 *  a language NAME inside its own sentence, so an English fallback put "Korean"
 *  in the middle of a Japanese one — the same defect that produced
 *  LANG_DISPLAY_FR, reported twice (2026-08-24). */
export const LANG_DISPLAY_JA: Record<string, string> = {
  en: "英語", vi: "ベトナム語", fr: "フランス語", ja: "日本語", zh: "中国語",
  yue: "広東語", ko: "韓国語", ru: "ロシア語", es: "スペイン語", de: "ドイツ語",
  it: "イタリア語", pt: "ポルトガル語", th: "タイ語", id: "インドネシア語", ms: "マレー語",
  tl: "タガログ語",
};
export const LANG_DISPLAY_ZH: Record<string, string> = {
  en: "英语", vi: "越南语", fr: "法语", ja: "日语", zh: "普通话",
  yue: "粤语", ko: "韩语", ru: "俄语", es: "西班牙语", de: "德语",
  it: "意大利语", pt: "葡萄牙语", th: "泰语", id: "印尼语", ms: "马来语",
  tl: "他加禄语",
};
/** Traditional Chinese labels for the `zh-Hant` UI locale. The chat's canned
 *  `zh` language keeps `LANG_DISPLAY_ZH` (Simplified), which also serves the
 *  `zh-Hans` UI locale. */
export const LANG_DISPLAY_ZH_HANS = LANG_DISPLAY_ZH;
// Generated from the zh-Hans table by `toTraditional`; fix meaning there, not here.
export const LANG_DISPLAY_ZH_HANT: Record<string, string> = {
  en: "英語", vi: "越南語", fr: "法語", ja: "日語", zh: "普通話",
  yue: "粵語", ko: "韓語", ru: "俄語", es: "西班牙語", de: "德語",
  it: "意大利語", pt: "葡萄牙語", th: "泰語", id: "印尼語", ms: "馬來語",
  tl: "他加祿語",
};
export const LANG_DISPLAY_KO: Record<string, string> = {
  en: "영어", vi: "베트남어", fr: "프랑스어", ja: "일본어", zh: "중국어",
  yue: "광둥어", ko: "한국어", ru: "러시아어", es: "스페인어", de: "독일어",
  it: "이탈리아어", pt: "포르투갈어", th: "태국어", id: "인도네시아어", ms: "말레이어",
  tl: "타갈로그어",
};

/**
 * Pick the display label for a language code in the active UI locale. VN
 * returns the "Tiếng …" form, FR the French name; anything else falls back to
 * the English label.
 */
export function langLabel(code: string, locale: DataLabelLocale = "en"): string {
  // Keyed on DataLabelLocale rather than Locale, because the chat runs in
  // languages the site has no UI for. Partial + an English base, so a new UI
  // locale falls back rather than failing to compile (see `LocaleTable`).
  const maps: Partial<Record<DataLabelLocale, Readonly<Record<string, string>>>> = {
    vi: LANG_DISPLAY_VI,
    fr: LANG_DISPLAY_FR,
    ja: LANG_DISPLAY_JA,
    zh: LANG_DISPLAY_ZH,
    ko: LANG_DISPLAY_KO,
    "zh-Hans": LANG_DISPLAY_ZH_HANS,
    "zh-Hant": LANG_DISPLAY_ZH_HANT,
  };
  const map = maps[locale] ?? LANG_DISPLAY;
  return map[code] ?? LANG_DISPLAY[code] ?? code;
}

/**
 * Ordered list of supported languages — single source of truth for both the
 * apply form's LanguageMultiSelect and the dashboard editor. Add a language
 * here and update no other file (LANG_DISPLAY/LANG_LABEL_TO_CODE/LANG_DISPLAY_CODES
 * also accept the same codes; keep them in sync).
 */
export const LANGUAGE_OPTIONS: { code: string; label: string }[] = [
  { code: "en",  label: "English" },
  { code: "vi",  label: "Vietnamese" },
  { code: "fr",  label: "French" },
  { code: "ja",  label: "Japanese" },
  { code: "ko",  label: "Korean" },
  { code: "zh",  label: "Mandarin" },
  { code: "yue", label: "Cantonese" },
  { code: "de",  label: "German" },
  { code: "ru",  label: "Russian" },
  { code: "es",  label: "Spanish" },
  { code: "it",  label: "Italian" },
  { code: "pt",  label: "Portuguese" },
  { code: "th",  label: "Thai" },
  { code: "id",  label: "Indonesian" },
  { code: "ms",  label: "Malay" },
  { code: "tl",  label: "Tagalog" },
];

/** Flat array of all valid language codes — useful for Zod enums / membership checks. */
export const LANGUAGE_CODES = LANGUAGE_OPTIONS.map((o) => o.code);

/** Display label (lowercase) → ISO code — for converting URL filter params */
export const LANG_LABEL_TO_CODE: Record<string, string> = {
  english: "en",    vietnamese: "vi", french: "fr",
  japanese: "ja",   mandarin: "zh",   cantonese: "yue",
  korean: "ko",     russian: "ru",    spanish: "es",
  german: "de",     italian: "it",    portuguese: "pt",
  thai: "th",       indonesian: "id", malay: "ms",
  tagalog: "tl",    filipino: "tl",
};

/**
 * The search filter value a `language` URL param stands for: an alias or a
 * different case ("Filipino", "tagalog") becomes the option's own value
 * ("Tagalog"), so the radio that is actually filtering shows as selected.
 * Anything unrecognised is returned unchanged.
 */
export function canonicalLanguageLabel(value: string): string {
  const code = LANG_LABEL_TO_CODE[value.trim().toLowerCase()];
  return code ? LANG_DISPLAY[code] : value;
}

/**
 * A language name shown ON ITS OWN, as a filter option, a select option or a
 * chip: first letter capitalised. `langLabel` stays lowercase for French
 * because the assistant puts names mid-sentence ("qui parlent coréen"); a
 * standalone label reads "Coréen" (owner, 2026-10-09: "capitalise in
 * filters"). Uses the locale's own casing rules; scripts without case
 * (Chinese, Japanese, Korean) come back unchanged.
 */
export function langLabelStandalone(code: string, locale: DataLabelLocale = "en"): string {
  const label = langLabel(code, locale);
  return label.charAt(0).toLocaleUpperCase(locale) + label.slice(1);
}

/**
 * Comma-joined display labels for a list of language codes, in the active
 * locale. Default locale is "en" so existing server-side callers (the
 * query layer) keep producing the same English string when no locale is
 * passed — locale-aware callers pass the active locale to flip to "Tiếng
 * Anh, Tiếng Việt".
 */
export function langCodesToDisplay(
  codes: string[],
  locale: Locale = "en",
): string {
  return codes.map((c) => langLabel(c, locale)).join(", ");
}

/** Display label (lowercase) → short display code (for compact mobile UI) */
const LANG_DISPLAY_CODES: Record<string, string> = {
  english: "EN",    vietnamese: "VI", french: "FR",
  japanese: "JA",   mandarin: "ZH",  cantonese: "YUE",
  korean: "KO",     russian: "RU",   spanish: "ES",
  german: "DE",     italian: "IT",   portuguese: "PT",
  chinese: "ZH",    thai: "TH",      indonesian: "ID",
  malay: "MS",      tagalog: "TL",   filipino: "TL",
};

/**
 * Normalise either an array of ISO codes or a legacy English comma-string
 * to a list of `{ code, isoCode }` pairs.
 *   - `code` is the 2-letter UPPERCASE short tag for the visible chip ("EN").
 *   - `isoCode` is the lowercase ISO key for the label maps ("en") — used
 *     to look up the locale-aware label.
 *
 * **Accepted inputs:** an array of ISO codes (preferred — `["en","vi"]`),
 * or a legacy English comma-string (`"English, Vietnamese"`).
 *
 * **Do NOT pass a locale-aware string** like `"Tiếng Anh, Tiếng Việt"` —
 * the legacy-string parse branch only knows English labels, so Vietnamese
 * tokens would silently fall through as their own raw lowercase strings.
 * Always reach for `pro.languageCodes` (raw codes from the query layer)
 * when both are available; only fall back to the English `pro.languages`
 * string when codes aren't provided.
 */
function normaliseLangInput(input: string | string[]): { code: string; isoCode: string }[] {
  const items = Array.isArray(input)
    ? input
    : input.split(",").map((l) => l.trim()).filter(Boolean);

  return items.map((item) => {
    const lc = item.toLowerCase();
    // If `item` is a valid ISO code (en, vi, fr, …), use it directly.
    if (LANG_DISPLAY[lc]) {
      return { code: lc.toUpperCase(), isoCode: lc };
    }
    // Otherwise treat as a legacy English label string ("English", "Vietnamese")
    // and resolve via the label→code map.
    const isoCode = LANG_LABEL_TO_CODE[lc] ?? lc;
    const shortCode = LANG_DISPLAY_CODES[lc] ?? item.slice(0, 2).toUpperCase();
    return { code: shortCode, isoCode };
  });
}

/**
 * Convert codes (or a legacy English string) to a short-form display: "EN / VI".
 * Locale-agnostic — the 2-letter short codes don't translate.
 */
export function toLangCodes(input: string | string[]): string {
  return normaliseLangInput(input).map((t) => t.code).join(" / ");
}

/**
 * Parse codes (or a legacy English string) into `{ code, label }` pairs.
 * `code` is the locale-agnostic 2-letter tag ("EN") for the visible chip;
 * `label` is the full locale-aware language name ("English" / "Tiếng Anh")
 * for tooltips and aria.
 */
export function toLangTokens(
  input: string | string[],
  locale: Locale = "en",
): { code: string; label: string }[] {
  return normaliseLangInput(input).map(({ code, isoCode }) => ({
    code,
    label: langLabel(isoCode, locale),
  }));
}
